import { SelectOption } from './types';

export const ACCOUNT_FILTER_GROUP = 'accountId';

function normalizeAccountIds(accountIds: string[]): string[] {
  return [...new Set(accountIds.map((id) => id.trim()).filter(Boolean))];
}

export function splitAccountFilters(filters: SelectOption[]): {
  filtersWithoutAccount: SelectOption[];
  accountIds: string[];
} {
  const filtersWithoutAccount = filters.filter((f) => f.group !== ACCOUNT_FILTER_GROUP);
  const accountIds = normalizeAccountIds(
    filters.filter((f) => f.group === ACCOUNT_FILTER_GROUP).map((f) => f.id)
  );

  return {
    filtersWithoutAccount,
    accountIds,
  };
}

export function splitProcessedFilterParams(processedFilters: string[]): {
  filtersWithoutAccount: string[];
  accountIds: string[];
} {
  const accountIds: string[] = [];
  const filtersWithoutAccount: string[] = [];

  processedFilters.forEach((rawFilter) => {
    try {
      const parsed = JSON.parse(rawFilter) as { field?: string; value?: string };
      if (parsed.field === ACCOUNT_FILTER_GROUP && typeof parsed.value === 'string') {
        accountIds.push(...parsed.value.split(','));
        return;
      }
    } catch {
      // Keep unparseable values untouched so existing URL compatibility is preserved.
    }

    filtersWithoutAccount.push(rawFilter);
  });

  return {
    filtersWithoutAccount,
    accountIds: normalizeAccountIds(accountIds),
  };
}

/**
 * Shared function to convert SelectOption filters into API filter strings
 * - For a single filter, uses op: 'equals'.
 * - For multiple values of the same field, groups by field and uses comma-separated values with op: 'in'.
 * Supports both single filters and arrays of filters.
 */
export function getApiFilters(f: SelectOption | SelectOption[]): string | string[] {
  if (!Array.isArray(f)) {
    const isTag = typeof f.group === 'string' && f.group.startsWith('tag:');
    let value = f.id;

    if (isTag) {
      // Extract the key from the group (remove 'tag:' prefix)
      const tagKey = f.group.substring(4); // Remove 'tag:' prefix
      // Remove the key and the colon from the id to get just the value
      const keyPrefix = `${tagKey}:`;
      value = f.id.startsWith(keyPrefix) ? f.id.substring(keyPrefix.length) : f.id;
    }

    return JSON.stringify({ field: f.group, value, op: 'equals' });
  }

  const groupedFilters = f.reduce(
    (acc, filter) => {
      if (!acc[filter.group]) {
        acc[filter.group] = [];
      }
      const isTag = typeof filter.group === 'string' && filter.group.startsWith('tag:');
      let value = filter.id;

      if (isTag) {
        // Extract the key from the group (remove 'tag:' prefix)
        const tagKey = filter.group.substring(4); // Remove 'tag:' prefix
        // Remove the key and the colon from the id to get just the value
        const keyPrefix = `${tagKey}:`;
        value = filter.id.startsWith(keyPrefix) ? filter.id.substring(keyPrefix.length) : filter.id;
      }

      acc[filter.group].push(value);
      return acc;
    },
    {} as Record<string, string[]>
  );

  // Convert to API format: single value -> equals; multiple values -> in
  return Object.entries(groupedFilters).map(([field, value]) => {
    if (field === 'autoscalingState') {
      // autoscalingState use equals with a comma-separated list
      return JSON.stringify({ field, value: value.join(','), op: 'equals' });
    }
    if (field === 'activeRuleState') {
      // activeRuleState should be converted to 'active' field with string value 'true'
      return JSON.stringify({ field: 'active', value: 'true', op: 'equals' });
    }
    return JSON.stringify({
      field,
      value: value.length === 1 ? value[0] : value.join(','),
      op: value.length > 1 ? 'in' : 'equals',
    });
  });
}
